import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@neo/primitives/dialog'
import type { SelectOption } from '@neo/primitives/select'
import { Typography } from '@neo/primitives/typography'
import dayjs from 'dayjs'
import { FileIcon, UploadIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useParams } from 'router-v6'
import { useProfileQuery, useTimezonesQuery } from 'v2/pages/settings/queries'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useFileUpload } from 'v2/shared/hooks/use-file-upload'
import { Views } from 'v2/types'
import type { RFileUploadResponse } from 'v2/types/api/auth'
import { AdhocChannels, type ConvChannels, type TaskForm } from 'v2/types/api/conversations'
import { useLiveAgentsAndTeams } from '../hooks/use-live-agents'
import { useActiveConversationsQuery } from '../queries'
import { getWrapupChildConversation, parseConversationURL } from '../utils'
import { useCreateTaskMutation } from './queries'
import { TaskFilePreview } from './task-components'
import {
  formatTaskDatetime,
  getTaskFields,
  TASK_DATE_TIME_FORMAT_TIMEZONE,
  TaskAssigneeType,
  TaskPriority,
  TaskStatus,
  TaskType,
} from './tasks.config'

export const TaskAttachment = ({ form }: { form: GenericFormType<TaskForm> }) => {
  const [attachments, setAttachments] = useState<RFileUploadResponse[]>([])
  const { uploadFile } = useFileUpload((file) => {
    setAttachments((prevAttachments) => {
      const newAttachments = [...prevAttachments, file]
      form.setFieldValue('attachments', newAttachments)
      return newAttachments
    })
  })
  const {
    getRootProps,
    getInputProps,
    open: openFileUpload,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt'],
      'video/mp4': ['.mp4'],
      'audio/mp3': ['.mp3'],
    },
    onDrop: (acceptedFiles) => {
      Array.from(acceptedFiles).forEach((file) => {
        uploadFile(file)
      })
    },
  })

  const onDelete = (attachment: RFileUploadResponse) => {
    setAttachments((prevAttachments) => {
      const filteredAttachments = prevAttachments.filter((a) => a.file_id !== attachment.file_id)

      form.setFieldValue('attachments', filteredAttachments)
      return filteredAttachments
    })
  }

  return (
    <div>
      <div
        className="flex items-center justify-center border border-dashed p-4 mb-2.5"
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <UploadIcon size={20} />
        <div className="flex flex-col items-center ml-4">
          <div className="flex items-center gap-1">
            <Typography variant="P/400" className="text-tertiary">
              Drag and drop your file(s) here or
            </Typography>
            <Button
              variant="link"
              type="button"
              className="text-link p-0 ring-offset-0"
              onClick={openFileUpload}
            >
              browse
            </Button>
          </div>
          <div className="flex items-baseline gap-1 mt-1 text-muted-foreground">
            <Typography variant="XS/400" className="text-tertiary">
              Max file size:
            </Typography>
            <Typography variant="XS/600" className="text-tertiary">
              5MB
            </Typography>
            <Typography variant="XS/400" className="text-tertiary">
              | File type:{' '}
            </Typography>
            <Typography variant="XS/600" className="text-tertiary">
              {['jpeg', 'png', 'mp4', 'mp3', 'pdf', 'txt', 'doc'].join(', ')}
            </Typography>
          </div>
        </div>
      </div>
      {attachments.length ? (
        <div className="flex gap-2 flex-wrap">
          {attachments.map((attachment: RFileUploadResponse) => (
            <TaskFilePreview key={attachment.file_id} attachment={attachment} onDelete={onDelete} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

export const CreateTaskModal = () => {
  const { conversationId } = useParams()
  const [isOpen, onOpenChange] = useState(false)
  const viewId = parseConversationURL()?.viewId
  // We will show the create task button only if the conversation is active or live
  const isActiveOrLive = viewId === Views.active || viewId === Views.live
  const { data: activeConversation } = useActiveConversationsQuery((d) =>
    d?.find((c) => c.conversation_number === conversationId),
  )
  const isWrapUp = activeConversation && getWrapupChildConversation(activeConversation)
  const formRef = useRef<GenericFormType<TaskForm> | null>(null)
  const { data: timezones } = useTimezonesQuery((data) =>
    data?.timezone_list
      ? (data.timezone_list.map((timezone) => ({
          label: timezone.name,
          value: timezone.tz_value,
        })) as SelectOption[])
      : [],
  )
  const {
    agents,
    teams,
    refetch: refetchLiveAgentsAndTeams,
  } = useLiveAgentsAndTeams(AdhocChannels.TASK, '')
  const {
    mutate: createTask,
    isPending,
    isSuccess,
  } = useCreateTaskMutation(conversationId as string)
  const { data: profileData } = useProfileQuery()
  const currentAgentId = profileData?.organizations[0].id
  const agentTimezone = profileData?.organizations[0].timezone || dayjs.tz.guess()

  useEffect(() => {
    if (isSuccess && !isPending) {
      onOpenChange(false)
    }
  }, [onOpenChange, isSuccess, isPending])

  useEffect(() => {
    if (isOpen) {
      refetchLiveAgentsAndTeams()
    }
  }, [isOpen, refetchLiveAgentsAndTeams])

  const defaultTaskFormValues = useMemo(
    () => ({
      task_type: TaskType.internal,
      channel: '' as ConvChannels,
      schedule: undefined,
      timezone: agentTimezone,
      title: '',
      description: '',
      assignee_type: TaskAssigneeType.self,
      assignee: currentAgentId as string,
      dueDate: undefined,
      priority: TaskPriority.medium,
      status: TaskStatus.todo,
      attachments: null,
    }),
    [agentTimezone, currentAgentId],
  )

  const handleCreateTask = (formData: TaskForm) => {
    const isFollowUp = formData.task_type === TaskType.followUp
    createTask({
      existing_conversation_number: conversationId as string,
      type: formData.task_type,
      channel: isFollowUp ? formData.channel : null,
      schedule_data: isFollowUp
        ? {
            schedule_start_time_local: dayjs(formData.schedule)
              .tz(formData.timezone as string)
              .format(TASK_DATE_TIME_FORMAT_TIMEZONE),
            task_schedule_time_type: 'custom',
            timezone: formData.timezone as string,
          }
        : null,
      task_data: {
        title: formData.title || '',
        description: formData.description,
      },
      source_type: TaskAssigneeType.human_agent,
      destination_type: formData.assignee_type,
      destination_id: formData.assignee,
      status: formData.status,
      priority: formData.priority,
      due_date:
        formData.task_type === TaskType.followUp
          ? formatTaskDatetime(formData.dueDate as Date, formData.timezone)
          : formatTaskDatetime(formData.dueDate as Date, agentTimezone),
      attachments: formData.attachments || null,
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger>
        {isActiveOrLive && !isWrapUp && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            tooltipContent="Create Task"
            data-testid="button-create-task"
          >
            <FileIcon className="size-[13px]" />
            New task
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[650px] p-6 overflow-y-auto max-h-[90vh] max-w-[90vw]">
        <DialogHeader className="flex flex-row justify-between">
          <DialogTitle>Create task</DialogTitle>
        </DialogHeader>
        <GenericForm<TaskForm>
          className="grid grid-cols-6 gap-4 lg:flex-1 xl:flex-[0.75] max-w-3xl"
          defaultValues={defaultTaskFormValues}
          fields={getTaskFields(
            timezones || [],
            agents || [],
            teams || [],
            currentAgentId as string,
          )}
          id="task-form"
          includeSubmitButton={false}
          onSubmit={handleCreateTask}
          onMount={(form) => {
            formRef.current = form
          }}
        />
        <DialogFooter className="flex flex-row sm:justify-between">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <FormSubmitButton formId="task-form" disabled={isPending}>
            <span>Create</span>
          </FormSubmitButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
